import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DataTableColumnHeader } from "@/components/admin/UserTable/data-table-column-header";
import { ReadSuperusersSuperusersGetResponse } from "@/client-admin/types.gen.ts";
import { SUPERUSER_PERMISSIONS } from "@/lib/utils";
import { DeactivateSuperUserDialog } from "./DeactivateSuperUserDialog";
import { EditSuperUserDialog } from "./EditSuperUserDialog";

export type SuperUserWithClientLinks = ReadSuperusersSuperusersGetResponse["data"][number];

function permissionLabel(permissionId: string) {
  return SUPERUSER_PERMISSIONS.find((permission) => permission.id === permissionId)?.label ?? permissionId;
}

export const columns: ColumnDef<SuperUserWithClientLinks>[] = [
  {
    accessorKey: "email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    cell: ({ row }) => <div>{row.getValue("email")}</div>,
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "is_active",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Active" />,
    cell: ({ row }) => <div>{row.getValue("is_active") ? "Yes" : "No"}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "last_login_at",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Login" />,
    cell: ({ row }) => {
      const lastLogin = row.getValue("last_login_at");

      if (typeof lastLogin != "string") return <div>-</div>;

      const date = new Date(lastLogin.endsWith("Z") ? lastLogin : `${lastLogin}Z`);

      return <div>{format(date, "yyyy-MM-dd:HH:mm")}</div>;
    },
  },
  {
    accessorKey: "client_links",
    header: "Permissions",
    cell: ({ row }) => {
      const permissions = row.original.client_links.flatMap((link) => link.permissions);

      if (permissions.length === 0) return <div>-</div>;

      if (permissions.length <= 2) {
        return <div>{permissions.map(permissionLabel).join(", ")}</div>;
      }

      return (
        <Popover>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 hover:underline">
              <span className="max-w-[150px] truncate">{permissionLabel(permissions[0])}</span>
              <Badge variant="secondary" className="cursor-pointer">
                +{permissions.length - 1}
              </Badge>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">All Permissions ({permissions.length})</h4>
              <div className="max-h-[200px] space-y-1 overflow-y-auto">
                {permissions.map((permission) => (
                  <div key={permission} className="py-1 text-sm">
                    {permissionLabel(permission)}
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      );
    },
    size: 200,
  },
  {
    accessorKey: "first_name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="First Name" />,
    cell: ({ row }) => <div>{row.getValue("first_name") || "-"}</div>,
  },
  {
    accessorKey: "last_name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Name" />,
    cell: ({ row }) => <div>{row.getValue("last_name") || "-"}</div>,
  },
  {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <div className="flex gap-2">
        <EditSuperUserDialog
          key={`${row.original.id}-${new Date().getTime()}`}
          id={row.original.id}
          email={row.original.email}
          first_name={row.original.first_name}
          last_name={row.original.last_name}
          is_active={row.original.is_active}
          client_links={row.original.client_links}
        />
        <DeactivateSuperUserDialog userId={row.original.id} email={row.original.email} />
      </div>
    ),
  },
];
